import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiAlertOctagon, FiArrowLeft } from "react-icons/fi";

import useBroadcastDetail from "./useBroadcastDetail";
import useBroadcastRecipients from "./useBroadcastRecipients";
import { ChannelFunnel } from "./ChannelFunnel";
import { CombinedLine } from "./CombinedLine";
import { RecipientsTab } from "./RecipientsTab";
import { EngagementTab } from "./EngagementTab";
import { RetractDialog } from "./RetractDialog";
import { BroadcastEmpty } from "./BroadcastEmpty";
import { Card } from "./ui/Card";
import { Tabs } from "./ui/Tabs";
import { BroadcastStatusBadge } from "./ui/Badge";
import { statusMeta, isRetractable, BANNER_COPY } from "./broadcastStatus";
import {
  CHANNEL_BY_ID,
  EMAIL_SEGMENT_BY_ID,
  PUSH_SEGMENT_BY_ID,
  ROUTE_BY_ID,
} from "./broadcastEnums";
import Loader from "../../../common/Loader/Loader";

const TABS = [
  { id: "recipients", label: "Recipients" },
  { id: "engagement", label: "Engagement" },
];

/** "4 Aug 2026, 09:30", or "" when the instant is missing or unparseable. */
const formatSentAt = (iso) => {
  if (!iso) return "";
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** The one-line summary under the title: channels · destination · when. */
const describe = (row) => {
  if (!row) return "";
  const parts = [
    CHANNEL_BY_ID[row.channels]?.label,
    ROUTE_BY_ID[row.routeKey] ? `opens ${ROUTE_BY_ID[row.routeKey].label}` : null,
    formatSentAt(row.scheduledAt || row.createdAt),
  ];
  return parts.filter(Boolean).join(" · ");
};

/**
 * One broadcast's delivery funnel and its recipients.
 *
 * Deliberately NOT built here: the Email-delivery bounce log, the per-link CTR
 * table and the CSV export. They exist in the API and in the design, and they
 * are out of scope for this build.
 */
export default function BroadcastAnalytics() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState("recipients");
  const [state, setState] = useState("delivered");
  const [engagement, setEngagement] = useState("clicked");
  const [query, setQuery] = useState("");
  const [confirmingRetract, setConfirmingRetract] = useState(false);

  const {
    funnel,
    broadcast,
    loading,
    refreshing,
    error,
    notFound,
    forbidden,
    retract,
    retracting,
    retractNote,
  } = useBroadcastDetail(id);

  // Only the visible tab fetches. Both tabs read the same endpoint with
  // different parameters, so loading the hidden one would double every request
  // for a list nobody is looking at.
  const recipientsData = useBroadcastRecipients(id, {
    state,
    query,
    enabled: tab === "recipients",
  });
  const engagementData = useBroadcastRecipients(id, {
    engagement,
    query,
    enabled: tab === "engagement",
  });

  if (forbidden) {
    return (
      <main className="min-h-screen bg-bc-page px-10 py-9">
        <BroadcastEmpty variant="forbidden" />
      </main>
    );
  }

  if (notFound) {
    // 404 rather than 403 across a tenant boundary is deliberate on the API's
    // side — softening it into "no permission" would confirm the broadcast
    // exists. Say it is not found and nothing more.
    return (
      <main className="min-h-screen bg-bc-page px-10 py-9">
        <BroadcastEmpty variant="error" message="Broadcast not found." />
      </main>
    );
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-bc-page px-10 py-9">
        <Loader />
      </main>
    );
  }

  const meta = statusMeta(broadcast);
  const banner = meta.banner ? BANNER_COPY[meta.key] : null;
  const pushSegmentLabel =
    PUSH_SEGMENT_BY_ID[broadcast?.pushSegment]?.label || broadcast?.pushSegment;
  const emailSegmentLabel =
    EMAIL_SEGMENT_BY_ID[broadcast?.emailSegment]?.label || broadcast?.emailSegment;

  return (
    <main className="min-h-screen bg-bc-page px-10 py-9">
      <div className="mx-auto max-w-[1120px]">
        <button
          type="button"
          onClick={() => navigate("/broadcasts")}
          className="mb-4 inline-flex items-center gap-1.5 text-xs text-bc-muted hover:text-bc-text"
        >
          <FiArrowLeft aria-hidden="true" />
          Broadcasts
        </button>

        <header className="mb-5 flex items-start justify-between gap-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <h1 className="truncate text-[26px] font-semibold tracking-[-0.02em] text-bc-text">
                {broadcast?.title || "Broadcast"}
              </h1>
              <BroadcastStatusBadge row={broadcast} />
            </div>
            <p className="mt-1 text-xs text-bc-soft">{describe(broadcast)}</p>
          </div>
          {isRetractable(broadcast) ? (
            <button
              type="button"
              onClick={() => setConfirmingRetract(true)}
              className="shrink-0 rounded-[7px] border border-bc-border bg-white px-4 py-2 text-[13px] font-medium text-bc-bad"
            >
              Retract
            </button>
          ) : null}
        </header>

        {banner ? (
          <div
            role="alert"
            className="mb-4 flex items-start gap-2.5 rounded-lg border border-bc-bad/40 bg-bc-bad-bg px-4 py-3 text-[13px] text-bc-bad"
          >
            <FiAlertOctagon className="mt-0.5 shrink-0" aria-hidden="true" />
            <p>{banner}</p>
          </div>
        ) : null}

        {error ? (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-bc-bad/40 bg-bc-bad-bg px-4 py-3 text-[13px] text-bc-bad"
          >
            {error}
          </div>
        ) : null}

        {/*
          The funnel updates itself every 15s while a send is in progress.
          `aria-live="polite"` announces the change without stealing focus — a
          100k send takes hours, and someone may be reading or tabbing through
          this region the whole time. The attribute sits on a wrapper because
          `Card` forwards only children/className/as by design.
        */}
        <section aria-live="polite" className="mb-4">
          <Card className="p-6">
            {refreshing ? (
              <p className="mb-3 text-xs text-bc-faint">Updating…</p>
            ) : null}
            <ChannelFunnel
              channel="push"
              stages={funnel?.push}
              note={funnel?.notes?.opens}
              segment={pushSegmentLabel}
            />
            {funnel?.push && funnel?.email ? (
              <hr className="my-6 border-bc-hairline" />
            ) : null}
            <ChannelFunnel
              channel="email"
              stages={funnel?.email}
              note={funnel?.notes?.opens}
              segment={emailSegmentLabel}
            />
            {funnel?.combined ? (
              <>
                <hr className="my-6 border-bc-hairline" />
                <CombinedLine combined={funnel.combined} />
              </>
            ) : null}
          </Card>
        </section>

        <Tabs value={tab} onChange={setTab} items={TABS} label="Recipient views" />

        <div className="mt-4">
          {tab === "recipients" ? (
            <RecipientsTab
              recipients={recipientsData.recipients}
              loading={recipientsData.loading}
              error={recipientsData.error}
              total={recipientsData.total}
              filter={state}
              onFilterChange={setState}
              query={query}
              onQueryChange={setQuery}
            />
          ) : (
            <EngagementTab
              recipients={engagementData.recipients}
              loading={engagementData.loading}
              error={engagementData.error}
              total={engagementData.total}
              filter={engagement}
              onFilterChange={setEngagement}
              query={query}
              onQueryChange={setQuery}
            />
          )}
        </div>
      </div>

      <RetractDialog
        open={confirmingRetract || Boolean(retractNote)}
        funnel={funnel}
        retracting={retracting}
        note={retractNote}
        onConfirm={retract}
        onCancel={() => setConfirmingRetract(false)}
      />
    </main>
  );
}
